/* omn-colour-param.js — light-PDP "?colour=<ral-slug>" deep-link reader.
   FEED-NOT-VARIANTS pilot (see omnitub-ads/FULL-RAL-FEED-ARCHITECTURE.md).

   Purely ADDITIVE. Loads on product pages, but does NOTHING unless BOTH:
     (a) the URL carries ?colour=<slug> — OR the natively-selected variant is a
         systematic RAL colour (the ?variant= → colour bridge, 7 Aug 2026: live
         Google Shopping colour offers link by ?variant=<vlid>, not ?colour=), AND
     (b) the current product handle is in PRODUCTS below (pilot gate).
   With no ?colour= and a White/non-colour selection the plain PDP is
   byte-for-byte unchanged — early return.

   ?variant= → COLOUR BRIDGE (7 Aug 2026, operator-authorised fix — closes the
   16/16 click-test failure in omnitub-ads/feed_cleanup/VERIFY-LANDING-STUDIO-CHAIN.md):
   when there is NO ?colour= param, read the NATIVELY-SELECTED variant's colour
   from the picker the theme already rendered server-side. Gated to systematic
   colours only (RAL nnnn / BS 4800). For RAL: paint the hero, show the preview
   line, and point the studio CTA at &colour=<slug> — but the native buy box is
   LEFT ALONE: the customer's clicked variant IS the purchasable entity at the
   correct price, so this path never selects the Custom-Colour carrier and never
   injects cart props (the variant's own title/SKU records the colour). For
   BS 4800 / legacy named colours no paint is possible (the colour asset is
   RAL-only) and the tub/tray studio cannot resolve them (it always loads the
   full RAL-only library and filters BS out) — those keep today's behaviour:
   correct coloured price, white viewer, stock CTA.
   PRECEDENCE: the customer's own later interactions always win — a manual
   picker change re-reads the selection (their new RAL repaints; anything else
   clears the bridge's dress), and a colour applied through the V1 palette
   stands the bridge's delayed hero re-fires down permanently.

   On a valid arrival it dresses the page to the clicked RAL, reusing the SAME
   mechanisms the OmniStudio configurator already uses (poka-yoke — one behaviour):
     1) HERO  — recolour the live 3D stage via the existing 'omn:colour' bridge
                (the PDP hero is a 3D <iframe id="omn-bath3d">, not a static <img>,
                 so we drive it the way the studio does; the tub shows the exact RAL
                 live — there is never a broken-image "?" state).
     2) PRICE — select the native "Custom Colour" carrier option so the theme's own
                dynamic price recomputes (sale-aware) exactly as a manual pick would.
     3) NAME  — show "RAL <code> <name>" next to the price.
     4) CART  — the carrier variant + a 'Requested colour' line-item property ride the
                add-to-cart (identical to the studio), so the order/invoice/packing
                slip capture the exact RAL. Re-injected at submit time as a guarantee.

   Colour data comes from the existing theme asset omnitub-colours-full.v1.json
   (the same canonical 2,042-RAL list the feed's R-index is built from) — no big
   per-colour metafield, so the PDP stays light. */
(function () {
  'use strict';

  var R2_BASE = 'https://pub-8eb6c435afe1489bb6a28f33682da483.r2.dev';

  // FULL FEED ROLLOUT (6 Aug 2026): ALL 73 full-RAL-feed products — the exact handle
  // set in omnitub-ads/prototypes/full-ral-feed/out/verification-table.tsv, skuBases
  // from the same table (column sku_stem, verified against the live R2 colour feed).
  // Was: 26 bath3d-certified deep baths only (2 Aug map) — that gate left the 5 Sheer,
  // 15 seated/specialist and 27 trays dead on ?colour= arrival (operator report 6 Aug).
  // Per-handle behaviour degrades gracefully by page furniture, not by this map:
  //   - 41 bath3d handles: 3D hero recolour + CTA to the dedicated studio
  //   - 26 'showers'-template trays: 3D stage recolour (id/bridge added in
  //     sections/main-product.liquid, 6 Aug) + CTA with &sys=classic
  //   - the rest (V1 in-page flow): photo-hero mask recolour where previewRenders
  //     exist; price/name/cart props always; CTA keeps ?omnistudio=open (+colour,
  //     inert until the V1 reader lands)
  var PRODUCTS = {
    // ── 26 deep baths (bath3d-certified — the original 2 Aug map) ──
    '1350-x-750-deep-soaking-tub-600mm-depth-rectangular-omnitub':                  { skuBase: 'DSB-O-GB-6215-00059' },
    'omnitub-1200-x-1200mm-extra-deep-bath':                                        { skuBase: 'DSB-O-GB-6215-00033' },
    'omnitub-1400-x-800mm-extra-deep-bath':                                         { skuBase: 'DSB-O-GB-6215-00034' },
    'omnitub-1600-x-1600mm-extra-deep-bath':                                        { skuBase: 'DSB-O-GB-6215-00035' },
    'omnitub-deluxe-1700-x-750mm-rectangular-deep-bath':                            { skuBase: 'DSB-O-GB-6215-00037' },
    'omnitub-deluxe-extra-1900-x-900mm-rectangular-deep-bath':                      { skuBase: 'DSB-O-GB-6215-00038' },
    'omnitub-deluxe-extreme-1750-x-950mm-rectangular-deep-bath':                    { skuBase: 'DSB-O-GB-6215-00039' },
    'omnitub-deluxe-plus-1800-x-800mm-rectangular-deep-bath':                       { skuBase: 'DSB-O-GB-6215-00040' },
    'omnitub-deluxe-ultra-1800-x-1050mm-rectangular-deep-bath':                     { skuBase: 'DSB-O-GB-6215-00041' },
    'omnitub-duo-1050-x-1050mm-small-square-deep-bath':                             { skuBase: 'DSB-O-GB-6215-00042' },
    'omnitub-duo-extra-1500-x-1050mm-small-rectangular-deep-bath':                  { skuBase: 'DSB-O-GB-6215-00043' },
    'omnitub-duo-extreme-1400-x-1400mm-large-square-deep-bath':                     { skuBase: 'DSB-O-GB-6215-00044' },
    'omnitub-duo-flow-1400-x-1200mm-large-deep-bath':                               { skuBase: 'DSB-O-GB-6215-00045' },
    'omnitub-duo-max-1600-x-1050mm-small-rectangular-deep-bath':                    { skuBase: 'DSB-O-GB-6215-00046' },
    'omnitub-duo-plus-1250-x-1050mm-small-rectangular-deep-bath':                   { skuBase: 'DSB-O-GB-6215-00047' },
    'omnitub-duo-ultra-1500-x-1500mm-large-square-deep-bath':                       { skuBase: 'DSB-O-GB-6215-00048' },
    'omnitub-solo-1050x-750mm-small-rectangular-deep-bath':                         { skuBase: 'DSB-O-GB-6215-00049' },
    'omnitub-solo-extra-1150-x-900mm-small-rectangular-deep-bath':                  { skuBase: 'DSB-O-GB-6215-00050' },
    'omnitub-solo-extreme-1600-x-800mm-small-rectangular-deep-bath':                { skuBase: 'DSB-O-GB-6215-00051' },
    'omnitub-solo-flow-1400-x-900mm-small-rectangular-deep-bath':                   { skuBase: 'DSB-O-GB-6215-00052' },
    'omnitub-solo-max-1050-x-800mm-small-rectangular-deep-bath':                    { skuBase: 'DSB-O-GB-6215-00053' },
    'omnitub-solo-plus-1250-x-750mm-small-rectangular-deep-bath':                   { skuBase: 'DSB-O-GB-6215-00054' },
    'omnitub-solo-supreme-1250-x-850mm-small-rectangular-deep-bath':                { skuBase: 'DSB-O-GB-6215-00055' },
    'omnitub-solo-sups-1300-x-950mm-small-rectangular-deep-bath':                   { skuBase: 'DSB-O-GB-6215-00056' },
    'omnitub-solo-ultra-1500-x-800mm-side-b-small-rectangular-deep-bath':           { skuBase: 'DSB-O-GB-6215-00057' },
    'omnitub-solo-ultra-1500-x-800mm-small-rectangular-deep-bath':                  { skuBase: 'DSB-O-GB-6215-00058' },
    // ── 15 seated & specialist (10 bath3d-certified; 5 serve the V1 in-page flow) ──
    '1100-x-700-matte-seated-soaking-tub-1000mm-depth-rectangular-omnitub':         { skuBase: 'MST-O-GB-6215-00028' },
    '1500-round-deep-bathtub-with-seats':                                           { skuBase: 'DST-O-GB-6215-00010' },
    '1800-x-1800-multi-person-deep-soaking-tub':                                    { skuBase: 'MP1800' },
    '3000-x-2000-step-entry-plunge-soaking-tub':                                    { skuBase: 'ODB3000' },
    'deep-bathtub-with-four-seats-1500-l-x-500-w-x-1000-depth-handmade-by-omnitub': { skuBase: 'DST-O-GB-6215-00026' },
    'deep-seated-tub-1100-l-x-600-w-x-1000-h-japanese-soaking-tub-from-omnitub':    { skuBase: 'DST-O-GB-6215-00027' },
    'deep-seated-tub-1100-l-x-700-w-x-1000-h-japanese-soaking-tub-from-omnitub':    { skuBase: 'DST-O-GB-6215-00028' },
    'deep-seated-tub-1350x700x1000':                                                { skuBase: 'DST-O-GB-6215-00029' },
    'deep-seated-tub-1495-l-x-800-w-x-1000-h-japanese-soaking-tub-from-omnitub':    { skuBase: 'DST-O-GB-6215-00030' },
    'deep-seated-tub-1500-l-x-700-w-x-1000-h-japanese-soaking-tub-from-omnitub':    { skuBase: 'DST-O-GB-6215-00031' },
    'omnitub-1100-x-1100mm-deep-seated-bath':                                       { skuBase: 'DST-O-GB-6215-00032' },
    'plunge-pool-from-omnitub':                                                     { skuBase: 'DPB-O-GB-6215-00059' },
    'repose-1900-reclined-soaking-tub':                                             { skuBase: 'REP1900' },
    'stepped-plunge-bath-1000-l-x-1000-w-x-1500-soaking-depth-handmade-by-omnitub': { skuBase: 'DPB-O-GB-6215-00060' },
    'two-person-deep-seated-bath-tub-1500-long-700-wide':                           { skuBase: 'DST-O-GB-6215-00061' },
    // ── 5 Sheer (bath3d-certified 29 Jul) ──
    'omnitub-sheer-1000':                                                           { skuBase: 'SHR1000' },
    'omnitub-sheer-1100':                                                           { skuBase: 'SHR1100' },
    'omnitub-sheer-1200':                                                           { skuBase: 'SHR1200' },
    'omnitub-sheer-1400':                                                           { skuBase: 'SHR1400' },
    'omnitub-sheer-900':                                                            { skuBase: 'SHR900' },
    // ── 27 shower trays (classic 3D studio, sys=classic) ──
    '1100-x-1000-shower-tray-rectangular-omnitub':                                  { skuBase: 'OST-O-GB-6215-00001' },
    '1100-x-800-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-6215-00002' },
    '1200-x-1000-omnitub-shower-tray':                                              { skuBase: 'OST-O-GB-6215-00003' },
    '1200-x-800-shower-tray-rectangular-omnitub':                                   { skuBase: 'OST-O-GB-6215-00004' },
    '1300-x-700-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-6215-00005' },
    '1300-x-900-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-6215-00006' },
    '1400-x-1400-omnitub-shower-tray':                                              { skuBase: 'OST-O-GB-6215-00007' },
    '1430-x-1210-shower-tray-rectangular-omnitub':                                  { skuBase: 'OST-O-GB-6215-00008' },
    '1450-x-1210-omnitub-shower-tray':                                              { skuBase: 'OST-O-GB-6215-00009' },
    '1500-x-1100-omnitub-shower-tray':                                              { skuBase: 'OST-O-GB-6215-00011' },
    '1500-x-1500-omnitub-shower-tray':                                              { skuBase: 'OST-O-GB-6215-00012' },
    '1500-x-800-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-6215-00013' },
    '1500-x-900-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-6215-00014' },
    '1550-x-800-shower-tray-rectangular-omnitub':                                   { skuBase: 'OST-O-GB-6215-00015' },
    '1600-x-800-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-6215-00016' },
    '1650-x-1100-omnitub-shower-tray':                                              { skuBase: 'OST-O-GB-6215-00017' },
    '1700-x-800-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-6215-00018' },
    '1800-x-1000-shower-tray-rectangular-omnitub':                                  { skuBase: 'OST-O-GB-6215-00023' },
    '1800-x-1350-omnitub-shower-tray':                                              { skuBase: 'OST-O-GB-6215-00019' },
    '1850-x-900-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-6215-00026' },
    '2000-x-1000-omnitub-shower-tray':                                              { skuBase: 'OST-O-GB-6215-00020' },
    '2260-x-1000-shower-tray-extra-large-rectangular-omnitub':                      { skuBase: 'OST-O-GB-6215-00021' },
    '2550-x-900-omnitub-shower-tray':                                               { skuBase: 'OST-O-GB-2590-00028' },
    '700-x-700-omnitub-shower-tray':                                                { skuBase: 'OST-O-GB-7070-00027' },
    '740-x-590-omnitub-shower-tray':                                                { skuBase: 'OST-O-GB-6215-00022' },
    '800-x-800-omnitub-handmade-shower-tray':                                       { skuBase: 'OST-O-GB-6215-00024' },
    '900-x-775-omnitub-shower-tray':                                                { skuBase: 'OST-O-GB-6215-00025' },
    // ── bath panels (P2b, 6 Sep 2026 — first panel's 2,042 full-RAL colour offers). Renders are
    //    keyed <sku_stem>-R<NNNN>.jpg on R2 (omnitub-ads/render_panel_fullral.py), so skuBase is
    //    the panel's shared SKU stem. Panel PDPs have no product <form>: the buy path is the
    //    dims-gated Size Builder -> V1 colour studio (omni-configurator.js), whose own reader
    //    picks the same ?colour= / omn_colour::<handle> echo up and preselects the RAL on open.
    'made-to-measure-front-bath-panel':                                             { skuBase: 'DBP-FR' }
  };

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  function slugify(code) {
    // MUST match build_solo_colour_feed.py: lowercase the code, non-alnum -> '-', trim '-'.
    return String(code).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function pad4(n) { n = String(n); while (n.length < 4) n = '0' + n; return n; }

  // Session-persist the deep-linked colour per product handle so it SURVIVES the theme's
  // ?colour -> ?variant URL rewrite (selectCarrier picks Custom Colour, which re-stamps the
  // URL and drops the param) and FOLLOWS the customer across reloads within the visit. A
  // fresh visitor with empty storage still gets the plain White PDP (operator 2 Aug 2026:
  // plain PDP = White by default; only an ad-arriver previews a colour).
  function storeKey(handle) { return 'omn_colour::' + handle; }
  function remember(handle, s) { try { sessionStorage.setItem(storeKey(handle), s); } catch (e) {} }
  function forget(handle) { try { sessionStorage.removeItem(storeKey(handle)); } catch (e) {} }
  function recall(handle) { try { return sessionStorage.getItem(storeKey(handle)); } catch (e) { return null; } }
  function titleCase(s) { return String(s).replace(/\b([a-z])/g, function (m, c) { return c.toUpperCase(); }); }

  // ── ?variant= → colour bridge: read the NATIVELY-SELECTED option value(s) ──
  // The variant in ?variant=<vlid> is already selected server-side, so the colour
  // name is on the page: the picker's selected option (custom-select), with a
  // native <select> fallback. Returns every selected option value (a product may
  // have more than one option); the colour gate below picks the systematic one.
  function selectedOptionValues() {
    var out = [];
    var vp = document.querySelector('variant-picker');
    if (!vp) return out;
    var opts = vp.querySelectorAll('.custom-select__option[aria-selected="true"]');
    for (var i = 0; i < opts.length; i++) out.push(opts[i].getAttribute('data-value') || opts[i].textContent || '');
    if (!out.length) {
      var sels = vp.querySelectorAll('select');
      for (var j = 0; j < sels.length; j++) {
        var s = sels[j];
        if (s.selectedIndex > -1 && s.options[s.selectedIndex]) out.push(s.options[s.selectedIndex].value || '');
      }
    }
    return out;
  }

  // Classify the natively-selected colour. SYSTEMATIC colours only (the tub/tray
  // twin of the waste flow's isSystematicColour() gate):
  //   {kind:'ral', slug} — RAL-coded, paintable (in the 2,042 RAL library);
  //   {kind:'bs'}        — BS 4800: systematic, but NOT in the RAL-only library
  //                        and NOT resolvable by the tub/tray studio → no dress;
  //   null               — White / Custom Colour / legacy named colours /
  //                        non-colour options → today's behaviour, untouched.
  function nativeColourSelection() {
    var vals = selectedOptionValues();
    for (var i = 0; i < vals.length; i++) {
      var v = String(vals[i]).replace(/\s+/g, ' ').trim();
      var m = v.match(/^(RAL \d+)\b/i);
      if (m) return { kind: 'ral', slug: slugify(m[1]), value: v };
      if (/^BS ?4800 /i.test(v)) return { kind: 'bs', value: v };
    }
    return null;
  }

  ready(function () {
    var CFG = window.__OMN_COLOUR_PARAM || {};
    // RAL licence §2(2): the colour JSON no longer carries RAL hex — the tub tint is
    // SAMPLED from the same-origin swatch sprite (omn-swatch.js). Configure it once.
    if (window.OmnSwatch && CFG.spriteUrl) OmnSwatch.cfg(CFG.spriteUrl, CFG.spriteCols, CFG.spriteRows);
    var handle = CFG.handle || (location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0];
    var prod = PRODUCTS[handle];
    if (!prod || !CFG.colourAsset) return;               // unknown product / no data -> no-op

    var LIB = null;
    function loadLib(cb) {
      if (LIB) { cb(LIB); return; }
      fetch(CFG.colourAsset)
        .then(function (r) { return r.json(); })
        .then(function (lib) { LIB = (lib && (lib.colours || lib)) || []; cb(LIB); })
        .catch(function () { /* fetch failed -> plain PDP stands (safe) */ });
    }

    // Dress the page for a slug. viaVariant=true is the ?variant= bridge: the
    // customer's clicked variant IS the purchasable entity at the correct price,
    // so the native buy box is left alone — NO selectCarrier() (diverting to the
    // generic carrier would discard the exact variant the ad sold) and NO cart
    // props (the variant's own title/SKU records the colour; an injected prop
    // could go stale if the customer re-picks). viaVariant=false is the original
    // ?colour= flow, unchanged.
    function dress(slug, viaVariant) {
      // P0 (operator field repro, 9 Aug 2026): the CTA repoint and the session
      // persist must NOT wait for the 448KB colour library — on Slow 3G that
      // fetch is a multi-second window in which a tap on any studio door left
      // with the stock href and the studio opened White. The slug is derivable
      // without the library, so both run FIRST; only the paint needs the data.
      // An unknown slug repoints harmlessly (the studio ignores it -> White).
      remember(handle, slug);
      studioCTA(handle, slug);
      loadLib(function (list) {
        var match = null, idx = -1;
        for (var i = 0; i < list.length; i++) {
          if (slugify(list[i].code) === slug) { match = list[i]; idx = i; break; }
        }
        if (!match) return;                              // unknown slug -> leave White default (safe)

        var code = String(match.code).replace(/\s+/g, ' ').trim();
        var name = String(match.name || '').trim();
        var label = (code + ' ' + name).trim();          // cart data verbatim: "RAL 5010 Gentian blue"
        var display = (code + ' ' + titleCase(name)).trim(); // shown to the eye: "RAL 5010 Gentian Blue"
        var rkey = prod.skuBase + '-R' + pad4(idx + 1);  // canonical list is 1-based in the feed
        var imgUrl = R2_BASE + '/' + rkey + '.jpg';

        // RAL tint sampled from the sprite (match.si); no RAL hex is published as data.
        if (window.OmnSwatch && match.si != null) {
          OmnSwatch.hex(match.si, function (hx) { dressHero(hx, name, code); });
        } else {
          dressHero(match.hex, name, code);              // BS/legacy fallback (carries hex)
        }
        showName(display);                               // a PREVIEW line, not a picker
        // PHOTO FIRST (28 Sep 2026): the bath PDP now opens on photos, the stage loads on tap. Offer the
        // hero this colour's own still (the same R2 render the cart line carries); the hero swaps only if
        // the image actually loads, so a missing render leaves the stock photo standing.
        stillFire(imgUrl, display);
        if (!viaVariant) {
          selectCarrier();                               // price + variant via the theme's own path
          wireCart(label, code, imgUrl);
        }
      });
    }

    // Clear everything the ?variant= bridge dressed — the customer picked
    // something that is not a paintable RAL, so the stock page must stand.
    function clearBridgeDress() {
      heroStand();                                       // stage back to White (active:false)
      stillFire(null, '');                               // hero photo back to the stock still
      var el = document.querySelector('[data-omn-colour-name]');
      if (el) el.remove();
      resetStudioCTA();
      forget(handle);                                    // P0 braces: a non-RAL pick must also
                                                         // stop the STUDIO inheriting the old
                                                         // slug via sessionStorage (classic.js
                                                         // reads omn_colour::<handle> when no
                                                         // ?colour= arrives)
    }

    // Customer's own later interactions ALWAYS win over the bridge (same
    // precedence rule as the deep-link stamping): any manual picker change
    // re-reads the selection — a new RAL repaints to THEIR pick; anything else
    // (White, Custom Colour, BS 4800, named) clears the bridge's dress. Only the
    // ?variant= bridge watches; the ?colour= flow's programmatic selectCarrier()
    // must never trip this, so it is not attached there.
    // DELEGATED at document level: the theme re-renders the product-info section
    // (replacing the variant-picker node) after a selection, so a listener on the
    // picker itself dies after the first change. apply() re-runs at a few delays
    // because that re-render can also land AFTER the first read and wipe the
    // freshly-dressed preview line — every part of dress() is idempotent.
    var pickerWatched = false;
    function watchNativePicker() {
      if (pickerWatched) return;
      pickerWatched = true;
      function apply() {
        var n = nativeColourSelection();
        if (n && n.kind === 'ral') dress(n.slug, true);
        else clearBridgeDress();
      }
      function applySoon() { [60, 700, 1800].forEach(function (t) { setTimeout(apply, t); }); }
      document.addEventListener('change', function (ev) {
        var t = ev.target;
        if (t && t.closest && t.closest('variant-picker')) applySoon();
      }, true);
      document.addEventListener('click', function (ev) {
        var t = ev.target;
        if (t && t.closest && t.closest('variant-picker') && t.closest('.custom-select__option')) applySoon();
      }, true);
    }

    function bridge(nat) {
      // BS 4800: systematic but unpaintable (RAL-only colour asset) and
      // unresolvable by the tub/tray studio (always-full RAL-only library,
      // classic.js noBS filter) — leave the page exactly as served: correct
      // coloured price, white viewer, stock CTA. Deliberately NO remembered-slug
      // fallback on any systematic arrival: the customer's newest explicit
      // choice (the variant they clicked) must not be overridden by an older
      // remembered RAL re-selecting the carrier.
      if (nat.kind !== 'ral') return;
      dress(nat.slug, true);
      watchNativePicker();
    }

    function fallbackRecall() {
      var r = recall(handle);                            // no param -> recover a remembered pick
      if (r) dress(r, false);
    }

    var slug;
    try { slug = new URLSearchParams(location.search).get('colour'); } catch (e) { slug = null; }
    if (slug) {                                          // fresh ?colour= ad-landing -> persist
      remember(handle, slug);
      dress(slug, false);
      return;
    }

    // ── ?variant= → colour bridge (7 Aug 2026) ──
    var nat = nativeColourSelection();
    if (nat) { bridge(nat); return; }
    var vparam = null;
    try { vparam = new URLSearchParams(location.search).get('variant'); } catch (e) {}
    if (vparam) {
      // A variant deep-link, but the custom-select may upgrade after
      // DOMContentLoaded — give it a few beats before concluding the selected
      // variant isn't a systematic colour.
      var tries = 0;
      var timer = setInterval(function () {
        var n = nativeColourSelection();
        if (n) { clearInterval(timer); bridge(n); }
        else if (++tries >= 8) { clearInterval(timer); fallbackRecall(); }
      }, 250);
      return;
    }
    fallbackRecall();
  });

  // Repoint the ONE colour doorway (the "Launch OmniStudio" pill in the media column) at the
  // studio PRESET to this RAL, and relabel it "Continue in OmniStudio" so the ad-arriver's
  // primary action carries their colour straight in. In the studio they can keep it or switch
  // to any other RAL (fully revertable) and it finalises + adds to cart there. Idempotent —
  // and RE-POINTABLE: the stock href/label are stored on first touch so a later dress (the
  // bridge repainting to the customer's new pick) REPLACES the slug instead of keeping the
  // stale one, and resetStudioCTA() can restore the stock pill exactly.
  // P0 widening (9 Aug 2026, operator field repro): repoint EVERY door into the
  // studio, not just the explainer pill — any /apps/studio anchor on the page and
  // the data-omn-go redirect target that ?omnistudio=open / trigger clicks honour
  // (assets/omni-configurator.js openOverlay guard). The explainer pill keeps its
  // "Continue in OmniStudio" relabel; other doors keep their own labels. The
  // embed=stage iframe src is deliberately NOT touched (the PDP drives the stage
  // live via postMessage; reloading the iframe would flash it).
  function withColour(href, slug) {
    return href + (href.indexOf('?') > -1 ? '&' : '?') + 'colour=' + encodeURIComponent(slug);
  }
  function studioDoors() {
    var out = [], seen = [];
    var els = document.querySelectorAll('.omst-x__cta, a[href^="/apps/studio"], [data-omn-go]');
    for (var i = 0; i < els.length; i++) { if (seen.indexOf(els[i]) === -1) { seen.push(els[i]); out.push(els[i]); } }
    return out;
  }
  function studioCTA(handle, slug) {
    var doors = studioDoors();
    for (var i = 0; i < doors.length; i++) {
      var el = doors[i];
      var isPill = el.className && String(el.className).indexOf('omst-x__cta') > -1;
      if (el.hasAttribute('data-omn-go')) {               // JS redirect door (buy-box trigger path)
        if (!el.hasAttribute('data-omn-go-stock')) el.setAttribute('data-omn-go-stock', el.getAttribute('data-omn-go'));
        el.setAttribute('data-omn-go', withColour(el.getAttribute('data-omn-go-stock'), slug));
      }
      if (el.tagName === 'A') {
        if (!el.hasAttribute('data-omn-cta-href')) {
          el.setAttribute('data-omn-cta-href', el.getAttribute('href') || ('/apps/studio?product=' + encodeURIComponent(handle)));
          el.setAttribute('data-omn-cta-label', el.textContent);
        }
        el.setAttribute('href', withColour(el.getAttribute('data-omn-cta-href'), slug)); // stock href never carries colour=
        if (isPill) el.textContent = 'Continue in OmniStudio';
      }
    }
  }
  function resetStudioCTA() {
    var doors = studioDoors();
    for (var i = 0; i < doors.length; i++) {
      var el = doors[i];
      if (el.hasAttribute('data-omn-go-stock')) el.setAttribute('data-omn-go', el.getAttribute('data-omn-go-stock'));
      if (!el.hasAttribute('data-omn-cta-href')) continue;
      el.setAttribute('href', el.getAttribute('data-omn-cta-href'));
      if (el.className && String(el.className).indexOf('omst-x__cta') > -1) {
        el.textContent = el.getAttribute('data-omn-cta-label') || 'Launch OmniStudio';
      }
    }
  }

  // 1) HERO — drive the live 3D stage through the existing 'omn:colour' bridge.
  //    The iframe loads async, so fire a few times; the bridge is idempotent.
  //    PRECEDENCE: fires read the CURRENT want (heroWant), so a re-dress redirects
  //    pending re-fires to the new colour, heroStand() silences them, and a colour
  //    the customer applies THEMSELVES via the V1 palette (omni-configurator.js is
  //    the only other 'omn:colour' dispatcher) stands this reader down permanently
  //    — no delayed re-fire may ever stomp a customer's own pick.
  function stillFire(src, label) {
    try { document.dispatchEvent(new CustomEvent('omn:colour-still', { detail: { src: src || null, label: label || '' } })); } catch (e) {}
  }
  var heroWant = null, heroWired = false, selfFire = false;
  function heroFire() {
    if (!heroWant) return;
    selfFire = true;
    try {
      document.dispatchEvent(new CustomEvent('omn:colour', {
        detail: { active: true, hex: heroWant.hex, name: heroWant.name, code: heroWant.code, system: '', family: '' }
      }));
    } catch (e) {}
    selfFire = false;
  }
  function heroStand() {                                 // back to White (bridge cleared)
    heroWant = null;
    selfFire = true;
    try {
      document.dispatchEvent(new CustomEvent('omn:colour', {
        detail: { active: false, hex: '', name: '', code: '', system: '', family: '' }
      }));
    } catch (e) {}
    selfFire = false;
  }
  function dressHero(hex, name, code) {
    heroWant = { hex: hex, name: name, code: code };
    heroFire();
    if (heroWired) return;
    heroWired = true;
    [400, 1200, 2500, 4000].forEach(function (t) { setTimeout(heroFire, t); });
    // also nudge once the 3D stage announces itself, if it ever does
    window.addEventListener('message', function (ev) {
      try {
        var d = ev && ev.data;
        if (d && (d.type === 'omn-ready' || d.type === 'omn-studio-ready')) heroFire();
      } catch (e) {}
    });
    // Foreign 'omn:colour' events (P0 revision, 9 Aug 2026):
    //  - V1's colour machinery (omni-configurator.js) stamps detail.sid on every
    //    dispatch, and it broadcasts at BOOT/hoist/takeover-reassert too -- not just
    //    on real picks. On stage PDPs (certified baths + trays) its overlay can never
    //    open (every open path redirects to the dedicated studio), so a sid-carrying
    //    event is machinery noise: a late one used to stand this reader down AND
    //    whitewash the freshly painted stage (field flakiness, reproduced 9 Aug).
    //    Now it RE-ASSERTS: we re-fire our colour right after, so whatever the fetch
    //    ordering, the stage ends on the dressed RAL.
    //  - a sid-less event we didn't dispatch = a genuinely unknown dispatcher ->
    //    conservative stand-down, as before.
    document.addEventListener('omn:colour', function (ev) {
      if (selfFire) return;
      var d = (ev && ev.detail) || {};
      if (d.sid !== undefined) { if (heroWant) setTimeout(heroFire, 60); return; }
      heroWant = null;
    });
  }

  // 2) PRICE + VARIANT — select the native "Custom Colour" carrier option so the
  //    theme recomputes the dynamic (sale-aware) price and points the form at the
  //    carrier variant, exactly like the studio's syncNative().
  function selectCarrier() {
    try {
      var vp = document.querySelector('variant-picker');
      var cs = vp && vp.querySelector('custom-select');
      if (cs && typeof cs.selectOption === 'function') {
        var opts = cs.querySelectorAll('.custom-select__option');
        for (var i = 0; i < opts.length; i++) {
          var v = (opts[i].getAttribute('data-value') || '').toLowerCase();
          if (v.indexOf('custom colour') > -1 || v.indexOf('custom color') > -1) {
            var cur = cs.querySelector('.custom-select__option[aria-selected="true"]');
            if (!cur || cur !== opts[i]) cs.selectOption(opts[i]);
            return;
          }
        }
      }
    } catch (e) {}
    // Fallback: point the main form at the carrier id + set the visible price directly.
    fallbackPrice();
  }

  function fallbackPrice() {
    var CFG = window.__OMN_COLOUR_PARAM || {};
    var form = mainForm();
    if (form && CFG.carrierId) {
      var idIn = form.querySelector('input[name="id"]');
      if (idIn) { idIn.value = String(CFG.carrierId); }
    }
    if (CFG.carrierPrice != null) {
      var val = document.querySelector('.product-price .price__current .js-value')
             || document.querySelector('.product-price .js-value');
      if (val) val.textContent = money(CFG.carrierPrice, CFG.moneyFormat);
      // The struck "was" was rendered by Liquid for the WHITE variant. Swap it to the
      // carrier's own compare-at — or blank it when there is no real saving — so the
      // hero never reads "£1,278 struck from £995" (a strike LOWER than the price;
      // false-strike found 6 Sep 2026). Labels + price--on-sale follow the same truth.
      var blk = (val && val.closest) ? val.closest('.price') : null;
      var was = blk ? blk.querySelector('.price__was .js-value') : null;
      if (was) {
        var cmp = Number(CFG.carrierCompareAt), cur = Number(CFG.carrierPrice);
        var onSale = isFinite(cmp) && cmp > cur;
        was.textContent = onSale ? money(cmp, CFG.moneyFormat) : '';
        var str = (window.theme && window.theme.strings) || {};
        var curLab = blk.querySelector('.price__current .js-label');
        var wasLab = blk.querySelector('.price__was .js-label');
        if (curLab && str.salePrice && str.regularPrice) curLab.textContent = onSale ? str.salePrice : str.regularPrice;
        if (wasLab) wasLab.textContent = (onSale && str.regularPrice) ? str.regularPrice : '';
        blk.classList.toggle('price--on-sale', onSale);
      }
    }
  }

  function money(cents, fmt) {
    var n = (Number(cents) / 100);
    var s = n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (!fmt) return '£' + s + ' GBP';
    return fmt.replace(/<[^>]*>/g, '').replace(/\{\{\s*amount\s*\}\}/g, s)
              .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, s);
  }

  // 3) NAME — a PREVIEW line (not a picker), shown once next to the price. The wording is
  //    deliberately "You're previewing …": the PDP previews the ad's colour, colour is
  //    chosen/changed in the OmniStudio (operator 2 Aug 2026).
  function showName(display) {
    var el = document.querySelector('[data-omn-colour-name]');
    if (!el) {
      var anchor = document.querySelector('.product-price') || document.querySelector('.product-info__price');
      if (!anchor) return;
      el = document.createElement('div');
      el.setAttribute('data-omn-colour-name', '');
      el.className = 'omn-colour-chosen product-info__block product-info__block--sm';
      el.style.cssText = 'margin:.4rem 0 .2rem;font-size:1.4rem;letter-spacing:.02em;color:#1E2C1F;';
      anchor.parentNode.insertBefore(el, anchor.nextSibling);
    }
    el.textContent = "You're previewing " + display;
  }

  // 4) CART — ensure the carrier variant + 'Requested colour' property ride the add.
  function mainForm() {
    return document.querySelector('.js-product-form-main')
        || document.querySelector('.js-product-form')
        || document.querySelector('form[action*="/cart/add"]');
  }
  function injectProps(label, code, imgUrl) {
    var form = mainForm();
    if (!form) return;
    var props = { 'Requested colour': label, '_colour_code': code, '_colour_image': imgUrl };
    form.querySelectorAll('input[data-omn-colour-prop]').forEach(function (n) { n.remove(); });
    Object.keys(props).forEach(function (k) {
      if (!props[k]) return;
      var inp = document.createElement('input');
      inp.type = 'hidden'; inp.name = 'properties[' + k + ']'; inp.value = props[k];
      inp.setAttribute('data-omn-colour-prop', '');
      form.appendChild(inp);
    });
  }
  function wireCart(label, code, imgUrl) {
    injectProps(label, code, imgUrl);
    // Re-inject at submit/click time in case a variant re-render cleared them (guarantee).
    var form = mainForm();
    if (form) {
      form.addEventListener('submit', function () { injectProps(label, code, imgUrl); }, true);
    }
    document.addEventListener('click', function (ev) {
      var t = ev.target;
      if (t && t.closest && t.closest('.add-to-cart, [name="add"], button[type="submit"]')) {
        injectProps(label, code, imgUrl);
      }
    }, true);
  }
})();
